import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDarkMode } from "../../../context/ThemeContext";
import { getThemeClasses } from "../../../utils/ThemeClasses";
import { StatCard } from "../v5/components/V5Charts";
import { WialonTokenModal } from "../../../Layouts/reseller-admin/components/sections/wialon-devices/components/WialonTokenModal";
import PlatformAlerts from "../../../components/PlatformAlerts";
import {
  useWialonDashboard,
} from "../../../features/dashboards/queries";
import {
  usePlatformCriticalAlerts,
  useReportingCompanies,
} from "../../../features/dashboards/reporting";
import ReportingUnitsTables from "../../../components/dashboards/ReportingUnitsTables";
import PlatformStatsCards from "../../../components/dashboards/PlatformStatsCards";
import PlatformRiskDistribution from "../../../components/dashboards/PlatformRiskDistribution";
import AllClientsTable from "../../../components/dashboards/AllClientsTable";
import ActiveUnitsHistorySection from "../../../components/dashboards/ActiveUnitsHistorySection";
import AltasBajasPlatformCards from "../../../components/dashboards/AltasBajasPlatformCards";

export default function WialonDashboard() {
  const { isDarkMode } = useDarkMode();
  const { t } = useTranslation("wialonDashboard");
  const themeClasses = getThemeClasses(isDarkMode);

  const wialonCompaniesQuery = useReportingCompanies({ platform: "wialon" });
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [wialonToken, setWialonToken] = useState(() => {
    return localStorage.getItem('wialonToken') || '';
  });
  const [lowBatteryPage, setLowBatteryPage] = useState(1);
  const [noDataPage, setNoDataPage] = useState(1);
  const [gpsIssuesPage, setGpsIssuesPage] = useState(1);
  const itemsPerPage = 10;

  const wialonQuery = useWialonDashboard(wialonToken);
  const devicesCount = wialonQuery.data?.devicesCount ?? 0;
  const usersCount = wialonQuery.data?.usersCount ?? 0;
  const devicesData = wialonQuery.data?.devicesData ?? [];
  const wialonError = wialonQuery.data?.wialonError ?? false;
  const loading = wialonQuery.isLoading;


  const handleSaveToken = (token: string) => {
    localStorage.setItem('wialonToken', token);
    setWialonToken(token);
  };

  // Process devices data for metrics
  const activeDevices = devicesData.filter((d: any) => d.act === 1).length;
  const inactiveDevices = devicesData.filter((d: any) => d.act === 0).length;

  // Devices with low battery list
  const devicesLowBatteryList = devicesData
    .filter((d: any) => d.hch?.low_battery?.period !== 'none')
    .map((d: any) => ({
      name: d.nm,
      uid: d.uid,
      period: d.hch.low_battery.period,
      company_id: d.crt,
      wialon_company_id: d.wialon_company_id,
      wialon_company_name: d.wialon_company_name,
    }));

  // Devices with no data list
  const devicesNoDataList = devicesData
    .filter((d: any) => d.hch?.no_data?.period !== 'none')
    .map((d: any) => ({
      name: d.nm,
      uid: d.uid,
      period: d.hch.no_data.period,
      company_id: d.crt,
      wialon_company_id: d.wialon_company_id,
      wialon_company_name: d.wialon_company_name,
    }));

  // Devices with GPS issues list
  const devicesGpsIssuesList = devicesData
    .filter((d: any) => 
      d.hch?.missing_position_data?.period !== 'none' || 
      d.hch?.insufficient_satellite_coverage?.period !== 'none'
    )
    .map((d: any) => ({
      name: d.nm,
      uid: d.uid,
      missing_position_period: d.hch?.missing_position_data?.period || 'none',
      satellite_coverage_period: d.hch?.insufficient_satellite_coverage?.period || 'none',
      company_id: d.crt,
      wialon_company_id: d.wialon_company_id,
      wialon_company_name: d.wialon_company_name,
    }));

  const criticalAlerts = usePlatformCriticalAlerts("wialon");
  const criticalCompanies = criticalAlerts.data?.companies ?? [];
  const allUnits = criticalAlerts.data?.units ?? [];

  // Helper function to generate page numbers with ellipsis
  const getPageNumbers = (totalPages: number, currentPage: number) => {
    const pages: (number | string)[] = [];
    const showPages = 5;

    if (totalPages <= showPages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="w-full h-full overflow-auto p-4 sm:p-6">
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className={`text-2xl sm:text-3xl font-bold ${themeClasses.text}`}>
            {t('wialonDashboard.title')}
          </h1>
          <p className={`text-sm ${themeClasses.textSecondary}`}>
            {t('wialonDashboard.description')}
          </p>
        </div>
      </div>

      {/* Wialon Error 7 Message */}
      {wialonError && (
        <div className={`mb-6 p-4 rounded-lg border bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800`}>
          <div className="flex items-start gap-3">
            <i className="ri-server-line text-orange-600 dark:text-orange-400 text-xl mt-0.5"></i>
            <div>
              <h3 className={`font-semibold text-orange-900 dark:text-orange-100 mb-1`}>
                {t('wialonDashboard.wialonPlatformError')}
              </h3>
              <p className={`text-sm text-orange-700 dark:text-orange-300`}>
                {t('wialonDashboard.wialonPlatformErrorMessage')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats normalizados (API nueva) */}
      <div className="mb-6 sm:mb-8">
        <PlatformStatsCards platform="wialon" themeClasses={themeClasses} />
      </div>

      {/* Stats Cards (legacy, oculto) */}
      {false && (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <StatCard
          icon={<i className="ri-cpu-line text-white text-sm"></i>}
          label={t('wialonDashboard.devices')}
          value={loading ? "..." : devicesCount}
          sublabel={t('wialonDashboard.total')}
          iconBgColor="bg-orange-500"
          themeClasses={themeClasses}
        />
        <StatCard
          icon={<i className="ri-user-line text-white text-sm"></i>}
          label={t('wialonDashboard.users')}
          value={loading ? "..." : usersCount}
          sublabel={t('wialonDashboard.total')}
          iconBgColor="bg-blue-500"
          themeClasses={themeClasses}
        />
        <StatCard
          icon={<i className="ri-check-line text-white text-sm"></i>}
          label="Activos"
          value={loading ? "..." : activeDevices}
          sublabel={`de ${devicesCount}`}
          iconBgColor="bg-green-500"
          themeClasses={themeClasses}
        />
        <StatCard
          icon={<i className="ri-close-line text-white text-sm"></i>}
          label="Inactivos"
          value={loading ? "..." : inactiveDevices}
          sublabel={`de ${devicesCount}`}
          iconBgColor="bg-red-500"
          themeClasses={themeClasses}
        />
      </div>
      )}

      {/* Distribución de riesgo por cliente */}
      <div className="mb-6 sm:mb-8">
        <PlatformRiskDistribution platform="wialon" themeClasses={themeClasses} />
      </div>

      <div className="mb-6 sm:mb-8">
        <ActiveUnitsHistorySection
          themeClasses={themeClasses}
          platform="wialon"
          platformLabel="Wialon"
          showActiveUnits={false}
        />
      </div>

      <div className="mb-6 sm:mb-8">
        <AltasBajasPlatformCards themeClasses={themeClasses} platform="wialon" />
      </div>

      {/* Reportan / No reportan (API nueva) */}
      <div className="mb-6 sm:mb-8">
        <ReportingUnitsTables platform="wialon" themeClasses={themeClasses} />
      </div>

      <div className="mb-6 sm:mb-8">
        <div className="mb-3 sm:mb-4">
          <h2 className={`text-lg sm:text-xl font-bold ${themeClasses.text}`}>
            Todos los clientes
          </h2>
          <p className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
            Listado completo de clientes Wialon con su categoría de servicio
          </p>
        </div>
        {wialonCompaniesQuery.isLoading ? (
          <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6 h-64 animate-pulse`}></div>
        ) : (
          <AllClientsTable
            platformName="Wialon"
            companies={wialonCompaniesQuery.data ?? []}
            themeClasses={themeClasses}
          />
        )}
      </div>

      {/* Tablas legacy de salud de dispositivos (batería / sin datos / GPS) — reemplazadas por la API nueva */}
      {false && (
      <>
      <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6 mb-6 sm:mb-8`}>
        <h3 className={`text-lg font-bold ${themeClasses.text} mb-4`}>{t('wialonDashboard.lowBatteryDevices')}</h3>
        {loading ? (
          <div className="space-y-3">
            <div className="h-8 bg-gray-200 dark:bg-gray-700/40 rounded animate-pulse"></div>
            <div className="h-8 bg-gray-200 dark:bg-gray-700/40 rounded animate-pulse"></div>
            <div className="h-8 bg-gray-200 dark:bg-gray-700/40 rounded animate-pulse"></div>
          </div>
        ) : devicesLowBatteryList.length === 0 ? (
          <p className={`text-sm ${themeClasses.textSecondary}`}>{t('wialonDashboard.noLowBatteryDevices')}</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b ${themeClasses.cardBorder}`}>
                    <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>{t('wialonDashboard.name')}</th>
                    <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>{t('wialonDashboard.imei')}</th>
                    <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>{t('wialonDashboard.clientId')}</th>
                    <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>{t('wialonDashboard.clientName')}</th>
                    <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>{t('wialonDashboard.period')}</th>
                  </tr>
                </thead>
                <tbody>
                  {devicesLowBatteryList
                    .slice((lowBatteryPage - 1) * itemsPerPage, lowBatteryPage * itemsPerPage)
                    .map((device, index) => (
                      <tr key={index} className={`border-b ${themeClasses.cardBorder}`}>
                        <td className={`py-2 px-3 ${themeClasses.text}`}>{device.name}</td>
                        <td className={`py-2 px-3 ${themeClasses.textSecondary}`}>{device.uid}</td>
                        <td className={`py-2 px-3 ${themeClasses.textSecondary}`}>{device.company_id || t('wialonDashboard.notAvailable')}</td>
                        <td className={`py-2 px-3 ${themeClasses.text}`}>{device.wialon_company_name || t('wialonDashboard.notFound')}</td>
                        <td className={`py-2 px-3 ${themeClasses.text}`}>{device.period}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {devicesLowBatteryList.length > itemsPerPage && (
              <div className="flex justify-center mt-6 space-x-2">
                <button
                  onClick={() => setLowBatteryPage(prev => Math.max(1, prev - 1))}
                  disabled={lowBatteryPage === 1}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-700 disabled:opacity-50"
                >
                  {t('wialonDashboard.previous')}
                </button>
                {getPageNumbers(Math.ceil(devicesLowBatteryList.length / itemsPerPage), lowBatteryPage).map((page, index) => (
                  <button
                    key={index}
                    onClick={() => typeof page === "number" && setLowBatteryPage(page)}
                    disabled={page === "..."}
                    className={`px-3 py-1 rounded ${
                      page === lowBatteryPage
                        ? "bg-primary-500 text-white"
                        : page === "..."
                          ? "bg-gray-200 text-gray-400 cursor-default"
                          : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setLowBatteryPage(prev => Math.min(Math.ceil(devicesLowBatteryList.length / itemsPerPage), prev + 1))}
                  disabled={lowBatteryPage === Math.ceil(devicesLowBatteryList.length / itemsPerPage)}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-700 disabled:opacity-50"
                >
                  {t('wialonDashboard.next')}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Devices with No Data */}
      <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6 mb-6 sm:mb-8`}>
        <h3 className={`text-lg font-bold ${themeClasses.text} mb-4`}>{t('wialonDashboard.noDataDevices')}</h3>
        {loading ? (
          <div className="space-y-3">
            <div className="h-8 bg-gray-200 dark:bg-gray-700/40 rounded animate-pulse"></div>
            <div className="h-8 bg-gray-200 dark:bg-gray-700/40 rounded animate-pulse"></div>
            <div className="h-8 bg-gray-200 dark:bg-gray-700/40 rounded animate-pulse"></div>
          </div>
        ) : devicesNoDataList.length === 0 ? (
          <p className={`text-sm ${themeClasses.textSecondary}`}>{t('wialonDashboard.noNoDataDevices')}</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b ${themeClasses.cardBorder}`}>
                    <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>{t('wialonDashboard.name')}</th>
                    <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>{t('wialonDashboard.imei')}</th>
                    <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>{t('wialonDashboard.clientId')}</th>
                    <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>{t('wialonDashboard.clientName')}</th>
                    <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>{t('wialonDashboard.period')}</th>
                  </tr>
                </thead>
                <tbody>
                  {devicesNoDataList
                    .slice((noDataPage - 1) * itemsPerPage, noDataPage * itemsPerPage)
                    .map((device, index) => (
                      <tr key={index} className={`border-b ${themeClasses.cardBorder}`}>
                        <td className={`py-2 px-3 ${themeClasses.text}`}>{device.name}</td>
                        <td className={`py-2 px-3 ${themeClasses.textSecondary}`}>{device.uid}</td>
                        <td className={`py-2 px-3 ${themeClasses.textSecondary}`}>{device.company_id || t('wialonDashboard.notAvailable')}</td>
                        <td className={`py-2 px-3 ${themeClasses.text}`}>{device.wialon_company_name || t('wialonDashboard.notFound')}</td>
                        <td className={`py-2 px-3 ${themeClasses.text}`}>{device.period}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {devicesNoDataList.length > itemsPerPage && (
              <div className="flex justify-center mt-6 space-x-2">
                <button
                  onClick={() => setNoDataPage(prev => Math.max(1, prev - 1))}
                  disabled={noDataPage === 1}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-700 disabled:opacity-50"
                >
                  {t('wialonDashboard.previous')}
                </button>
                {getPageNumbers(Math.ceil(devicesNoDataList.length / itemsPerPage), noDataPage).map((page, index) => (
                  <button
                    key={index}
                    onClick={() => typeof page === "number" && setNoDataPage(page)}
                    disabled={page === "..."}
                    className={`px-3 py-1 rounded ${
                      page === noDataPage
                        ? "bg-primary-500 text-white"
                        : page === "..."
                          ? "bg-gray-200 text-gray-400 cursor-default"
                          : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setNoDataPage(prev => Math.min(Math.ceil(devicesNoDataList.length / itemsPerPage), prev + 1))}
                  disabled={noDataPage === Math.ceil(devicesNoDataList.length / itemsPerPage)}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-700 disabled:opacity-50"
                >
                  {t('wialonDashboard.next')}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Devices with GPS Issues */}
      <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6 mb-6 sm:mb-8`}>
        <h3 className={`text-lg font-bold ${themeClasses.text} mb-4`}>Dispositivos con Problemas de GPS</h3>
        {loading ? (
          <div className="space-y-3">
            <div className="h-8 bg-gray-200 dark:bg-gray-700/40 rounded animate-pulse"></div>
            <div className="h-8 bg-gray-200 dark:bg-gray-700/40 rounded animate-pulse"></div>
            <div className="h-8 bg-gray-200 dark:bg-gray-700/40 rounded animate-pulse"></div>
          </div>
        ) : devicesGpsIssuesList.length === 0 ? (
          <p className={`text-sm ${themeClasses.textSecondary}`}>No hay dispositivos con problemas de GPS</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b ${themeClasses.cardBorder}`}>
                    <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>Nombre</th>
                    <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>IMEI</th>
                    <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>Cliente ID</th>
                    <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>Nombre Cliente</th>
                    <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>Posición Faltante</th>
                    <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>Cobertura Satelital</th>
                  </tr>
                </thead>
                <tbody>
                  {devicesGpsIssuesList
                    .slice((gpsIssuesPage - 1) * itemsPerPage, gpsIssuesPage * itemsPerPage)
                    .map((device, index) => (
                      <tr key={index} className={`border-b ${themeClasses.cardBorder}`}>
                        <td className={`py-2 px-3 ${themeClasses.text}`}>{device.name}</td>
                        <td className={`py-2 px-3 ${themeClasses.textSecondary}`}>{device.uid}</td>
                        <td className={`py-2 px-3 ${themeClasses.textSecondary}`}>{device.company_id || 'N/A'}</td>
                        <td className={`py-2 px-3 ${themeClasses.text}`}>{device.wialon_company_name || 'No encontrado'}</td>
                        <td className={`py-2 px-3 ${themeClasses.text}`}>{device.missing_position_period}</td>
                        <td className={`py-2 px-3 ${themeClasses.text}`}>{device.satellite_coverage_period}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {devicesGpsIssuesList.length > itemsPerPage && (
              <div className="flex justify-center mt-6 space-x-2">
                <button
                  onClick={() => setGpsIssuesPage(prev => Math.max(1, prev - 1))}
                  disabled={gpsIssuesPage === 1}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-700 disabled:opacity-50"
                >
                  Anterior
                </button>
                {getPageNumbers(Math.ceil(devicesGpsIssuesList.length / itemsPerPage), gpsIssuesPage).map((page, index) => (
                  <button
                    key={index}
                    onClick={() => typeof page === "number" && setGpsIssuesPage(page)}
                    disabled={page === "..."}
                    className={`px-3 py-1 rounded ${
                      page === gpsIssuesPage
                        ? "bg-primary-500 text-white"
                        : page === "..."
                          ? "bg-gray-200 text-gray-400 cursor-default"
                          : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setGpsIssuesPage(prev => Math.min(Math.ceil(devicesGpsIssuesList.length / itemsPerPage), prev + 1))}
                  disabled={gpsIssuesPage === Math.ceil(devicesGpsIssuesList.length / itemsPerPage)}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-700 disabled:opacity-50"
                >
                  Siguiente
                </button>
              </div>
            )}
          </>
        )}
      </div>
      </>
      )}

      {/* Wialon Token Modal */}
      <WialonTokenModal
        isOpen={showTokenModal}
        onClose={() => setShowTokenModal(false)}
        onSave={handleSaveToken}
        currentToken={wialonToken}
        themeClasses={themeClasses}
      />

      {/* Sección: Alertas Críticas */}
      <div className="mt-6 sm:mt-8">
        {criticalAlerts.isLoading ? (
          <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}>
            <div className="space-y-3">
              <div className="h-8 bg-gray-200 dark:bg-gray-700/40 rounded animate-pulse"></div>
              <div className="h-8 bg-gray-200 dark:bg-gray-700/40 rounded animate-pulse"></div>
              <div className="h-8 bg-gray-200 dark:bg-gray-700/40 rounded animate-pulse"></div>
            </div>
          </div>
        ) : (
          <PlatformAlerts
            companies={criticalCompanies}
            totalClients={wialonCompaniesQuery.data?.length ?? 0}
            allUnits={allUnits}
            platformName="Wialon"
            themeClasses={themeClasses}
            isDarkMode={isDarkMode}
          />
        )}
      </div>
    </div>
  );
}
